import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { FIFTEEN_MINUTES, QUERY_KEYS } from '@avanta/shared/constants/common';
import { AppointmentService } from '@avanta/shared/services/appointments';
import { PsychologistService } from '@avanta/shared/services/psychologists';
import { type Appointment, type SessionDetails } from '@avanta/shared/types/appointments';

export interface AppointmentUser {
  timeZone?: string | null;
}

export interface UseFetchNextAppointmentsOptions {
  patientId: string;
  psychologistId?: string;
  currentUser: AppointmentUser | null | undefined;
  enabled?: boolean;
}

const getSessionDetails = async (
  sessionId: string,
  appointment: Appointment
): Promise<SessionDetails> => {
  try {
    if (appointment.canceled) {
      return {} as SessionDetails;
    }
    return (await AppointmentService.getSessionDetails(sessionId)).data;
  } catch {
    return {} as SessionDetails;
  }
};

export const useFetchNextAppointments = ({
  patientId,
  psychologistId,
  currentUser,
  enabled = true,
}: UseFetchNextAppointmentsOptions): UseQueryResult<Appointment[]> => {
  const userTz = currentUser?.timeZone;

  return useQuery({
    queryKey: [QUERY_KEYS.FETCH_NEXT_APPOINTMENTS, patientId, psychologistId, userTz],
    queryFn: async () => {
      const appointments = await AppointmentService.getAppointments(patientId, psychologistId);
      const response = appointments.data.map(async appointment => ({
        ...appointment,
        psychologist: {
          psychologist: (await PsychologistService.getPsychologist(appointment.psychologistId))
            .data,
        },
        sessionDetails: await getSessionDetails(appointment.sessionId, appointment),
        center: (await AppointmentService.getCenterDetails(appointment.backofficeCenterId)).data,
      }));
      return Promise.all(response);
    },
    select: response =>
      response.toSorted(
        (firstAppointment, secondAppointment) =>
          new Date(firstAppointment.dateTime).getTime() -
          new Date(secondAppointment.dateTime).getTime()
      ),
    enabled: Boolean(patientId && currentUser && enabled),
    staleTime: FIFTEEN_MINUTES,
  });
};
